/**
 * Shared typography and vertical rhythm for public shop CMS pages.
 * Tuned for readable body copy (~16–17px) and tighter section spacing than earlier builds.
 */

/**
 * Body copy in RichText / columns / image+text rows. Uses {@link SHOP_CMS_BODY_FONT_CLASS} — not
 * Cormorant — so mixed English + Chinese in one paragraph shares one weight (Cormorant is Latin-only).
 */
export const SHOP_CMS_BODY_FONT_CLASS = 'shop-cms-body-font'

export const SHOP_CMS_BODY =
  `shop-cms-html ${SHOP_CMS_BODY_FONT_CLASS} [&_li]:text-base [&_li]:leading-normal sm:[&_li]:text-[1.0625rem] [&_ol]:mb-1 [&_ol]:mt-0 [&_ol]:pl-5 [&_p]:mb-2 [&_p]:mt-0 [&_p]:ml-0 [&_p]:indent-0 [&_p]:pl-0 [&_p]:text-indent-0 [&_p]:text-base [&_p]:leading-normal sm:[&_p]:text-[1.0625rem] [&_p]:first:mt-0 [&_p]:last:mb-0 [&_ul]:mb-1 [&_ul]:mt-0 [&_ul]:pl-5 [&_strong]:font-semibold`

export const SHOP_SECTION_GUTTER = 'px-6 sm:px-8'

export const SHOP_BLOCK_INNER_MAX = 'max-w-7xl'

/** Standalone section padding (single section or outside a stacked list). */
export const SHOP_MAIN_SECTION_V_PAD = 'py-6 sm:py-8'

/** Gap between stacked CMS sections — bottom padding only so neighbors do not double up. */
export const SHOP_SECTION_STACK_GAP = 'pb-6 sm:pb-8'

/**
 * Vertical padding for one section in a CMS section list.
 * @param embeddedInPageShell — page wrapper already has SHOP_PAGE_CONTENT_V_PAD (omit first top / last bottom).
 */
export function shopSectionStackVerticalPad(
  index: number,
  total: number,
  embeddedInPageShell = false,
): string {
  const isFirst = index === 0
  const isLast = index >= total - 1
  const top = isFirst && !embeddedInPageShell ? 'pt-6 sm:pt-8' : 'pt-0'
  const bottom =
    isLast && embeddedInPageShell ? 'pb-0' : isLast ? 'pb-6 sm:pb-8' : SHOP_SECTION_STACK_GAP
  return `${top} ${bottom}`
}

/** Outer wrapper on dynamic CMS pages (above section list). */
export const SHOP_PAGE_CONTENT_V_PAD = 'py-6 sm:py-8'

/** Section / block titles with full-width underline. */
export const SHOP_BLOCK_HEADING_CLASS =
  'mb-4 border-b-2 pb-2 text-base font-semibold uppercase tracking-[0.12em] text-balance sm:text-lg'

/** Image + text rows section label — tighter margin before first row. */
export const SHOP_BLOCK_HEADING_IMAGE_TEXT =
  'mb-2 border-b-2 pb-1.5 text-base font-semibold uppercase tracking-[0.12em] text-balance sm:text-lg'

/** Centered page title block with fixed-width rules above and below. */
export const SHOP_PAGE_TITLE_WRAP =
  'flex flex-col items-center gap-2.5 py-3 sm:gap-3 sm:py-4'

/**
 * Fixed rule width — calibrated to ~1.5× “Wedding Performance” at md title size.
 * Uses border-top (not background height) so top and bottom rules render the same 1px weight.
 */
export const SHOP_PAGE_TITLE_LINE =
  'm-0 box-border h-0 w-[21rem] max-w-full shrink-0 border-0 border-t border-solid p-0 [border-top-width:1px]'

/** Page title on dynamic CMS pages. */
export const SHOP_PAGE_TITLE_CLASS =
  'm-0 font-serif text-lg font-semibold leading-tight tracking-tight text-balance sm:text-xl md:text-[1.4rem]'

/** Tint primary for page title divider lines (~75% opacity). */
export function shopPageTitleLineColor(primaryColor: string): string {
  if (primaryColor.startsWith('#') && primaryColor.length === 7) {
    return `${primaryColor}BF`
  }
  return primaryColor
}

/** Border color for CMS page title rules (solid primary tint). */
export function shopPageTitleLineStyle(primaryColor: string): { borderTopColor: string } {
  return { borderTopColor: shopPageTitleLineColor(primaryColor) }
}

/** Multi-column “line after title” section heading. */
export const SHOP_SECTION_HEADING_LINE =
  'shrink-0 font-serif text-lg font-semibold tracking-tight text-balance sm:text-xl'

/** Sans-serif stack for catalog UI — most shops use 14–16px system/Inter on product cards, not display serifs. */
export const SHOP_CATALOG_UI = 'font-sans'

/**
 * Header nav / cart chrome — Cormorant (shop body serif) has no CJK glyphs, so Chinese menu
 * labels would otherwise fall back to inconsistent system serif/sans fonts per item.
 */
export const SHOP_NAV_FONT_STACK =
  "Inter, system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei UI', 'Noto Sans CJK SC', sans-serif"

/** Product grid layout — breathing room between cards, more columns on wide viewports. */
export const SHOP_PRODUCT_CARD_GRID =
  'grid gap-5 sm:grid-cols-2 sm:gap-6 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5'

/** Row list layout — group sidebar + stacked product rows. */
export const SHOP_CATALOG_ROWS_SHELL = 'flex flex-col gap-5 lg:flex-row lg:items-start'
export const SHOP_CATALOG_GROUP_SIDEBAR =
  'w-full shrink-0 rounded-lg border border-slate-200 bg-slate-50/80 p-3 lg:w-52 xl:w-56'
export const SHOP_CATALOG_GROUP_SIDEBAR_TITLE =
  'mb-2 hidden text-[10px] font-medium uppercase tracking-wide text-slate-500 lg:block'
export const SHOP_CATALOG_GROUP_SIDEBAR_NAV =
  'flex flex-row flex-wrap gap-1.5 lg:flex-col lg:gap-0.5'
export const SHOP_CATALOG_GROUP_SIDEBAR_ITEM =
  'rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-slate-700 transition hover:bg-white hover:text-slate-900 lg:w-full'
export const SHOP_CATALOG_GROUP_SIDEBAR_ITEM_ACTIVE =
  'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
export const SHOP_PRODUCT_ROW_LIST =
  'min-w-0 flex-1 divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white'

/** Embedded product-grid section heading (matches other CMS block titles). */
export const SHOP_PRODUCT_GRID_HEADING =
  'mb-4 border-b border-stone-300/80 pb-2 font-serif text-base font-semibold uppercase tracking-[0.12em] text-balance sm:text-lg'

/** Product catalog filter bar — compact sans-serif controls (typical e-commerce PLP toolbar). */
export const SHOP_CATALOG_FILTER_BAR =
  'mb-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 sm:p-3.5'

export const SHOP_CATALOG_FILTER_LABEL =
  'mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-500'

export const SHOP_CATALOG_FILTER_CONTROL =
  'w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400'

/** Dropdowns in the catalog filter bar — 13px (1px smaller than text inputs). */
export const SHOP_CATALOG_FILTER_SELECT =
  'w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400'

export const SHOP_CATALOG_FILTER_BUTTON =
  'rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50'

/** Brand intro when filtering catalog by list brand — typography only, no card or rules. */
export const SHOP_CATALOG_BRAND_INTRO = 'mb-5 pt-1'

/** Logo left of brand name on filtered catalog. */
export const SHOP_CATALOG_BRAND_INTRO_ROW =
  'flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-2'

export const SHOP_CATALOG_BRAND_LOGO =
  'h-[3.795rem] w-auto max-w-[15.18rem] shrink-0 object-contain'

export const SHOP_CATALOG_BRAND_INTRO_NAME =
  'min-w-0 font-serif text-lg font-semibold tracking-tight text-balance text-left sm:text-xl'

export const SHOP_CATALOG_BRAND_INTRO_TEXT =
  'mx-auto mt-2 max-w-2xl whitespace-pre-wrap text-center text-sm italic leading-relaxed text-stone-600'

export const SHOP_CATALOG_EMPTY_STATE =
  'rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500'

/** Product detail, cart, and checkout pages — aligned with CMS page vertical rhythm. */
export const SHOP_TRANSACTION_V_PAD = SHOP_PAGE_CONTENT_V_PAD

export const SHOP_TRANSACTION_TITLE =
  'mb-5 font-serif text-xl font-semibold tracking-tight sm:text-2xl'

export const SHOP_TRANSACTION_PRIMARY_BUTTON =
  'inline-flex justify-center rounded-md px-5 py-2 text-sm font-medium text-white disabled:opacity-60'

export const SHOP_TRANSACTION_SECONDARY_BUTTON =
  'inline-flex justify-center rounded-md border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'

/**
 * Storefront URL prefix. Custom domains use `''` (root-relative paths); shared host uses `/shop/:slug`.
 * Unlike `basePath || fallback`, this preserves an intentional empty string.
 */
export function shopStorefrontBasePath(basePath: string | undefined, slug: string): string {
  if (basePath !== undefined) return basePath
  return slug ? `/shop/${slug}` : ''
}

/** Canonical path for shop home (custom domain → `/`). */
export function shopStorefrontCanonicalPath(basePath: string | undefined, slug: string): string {
  const base = shopStorefrontBasePath(basePath, slug)
  return base === '' ? '/' : base
}

/** Storefront paths that must work when the shop is unpublished (direct payment links, returns). */
export const STOREFRONT_PAYMENT_HANDOFF_SUFFIXES = [
  '/airwallex-pay',
  '/demo-pay',
  '/checkout/return',
  '/checkout/confirmation',
] as const

export function storefrontPathSuffix(pathname: string, basePath: string): string {
  const normalizedBase = basePath.replace(/\/+$/, '') || ''
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  if (normalizedBase && !normalizedPath.startsWith(normalizedBase)) return normalizedPath
  const suffix = normalizedBase ? normalizedPath.slice(normalizedBase.length) : normalizedPath
  return suffix || '/'
}

export function isStorefrontPaymentHandoffRoute(pathname: string, basePath: string): boolean {
  const suffix = storefrontPathSuffix(pathname, basePath)
  return STOREFRONT_PAYMENT_HANDOFF_SUFFIXES.some(
    (segment) => suffix === segment || suffix.startsWith(`${segment}/`),
  )
}
